import { getManageDependentsForm } from 'api/operations/formService';
import { autorun, makeAutoObservable } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import SubDependentDelegate from 'store/modules/subscriber/subscriber-basic-info/dependents/subDependentDelegate';
import { useEffect, useState } from 'react';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types/dynamicForms';
import { DynamicFormDelegate } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { SubscriberPersonInfo } from '../types';
import SubscriberBasicInfoDelegate from '../subscriberBasicInfoDelegate';

interface DomainData {
  dependentDelegate: SubDependentDelegate;
  subscriberBasicInfo: SubscriberBasicInfoDelegate;
  formId: string;
}

export class UpdateDependentModalStore {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private readonly _dependentDelegate: SubDependentDelegate;
  private readonly _subscriberBasicInfo: SubscriberBasicInfoDelegate;
  private readonly _formId: string;
  private _updateDependentModal = new ModalDelegate();

  public dynamicJSONFormDelegate = new DynamicFormDelegate({
    currentUserRoles: this._rootStore.userStore.userRoles,
    fetchForm: async (formId: string): Promise<FormObjectFromJSON> => {
      return getManageDependentsForm(
        formId,
        {
          common: this._subscriberBasicInfo.primary,
          custom: {
            dependents: [this._dependentDelegate.dependent],
          },
        },
        this._dependentDelegate.dependent.id,
        this._rootStore.userStore.sponsorId,
      );
    },
    onSubmit: this.updateDependent.bind(this),
  });

  public get showUpdateDependentModal(): boolean {
    return this._updateDependentModal.isVisible;
  }

  public async openUpdateDependentModal(): Promise<void> {
    this._updateDependentModal.open();
    await this.dynamicJSONFormDelegate.buildForm(this._formId, { ...this._dependentDelegate.dependent });
  }

  public closeUpdateDependentModal(): void {
    this.dynamicJSONFormDelegate.resetState();
    this._updateDependentModal.close();
  }

  public async updateDependent(): Promise<void> {
    if (!this.dynamicJSONFormDelegate.formController) return;

    const updatedDependentInfo = {
      ...this.dynamicJSONFormDelegate.formAnswers,
      id: this._dependentDelegate.dependent.id,
    } as unknown as SubscriberPersonInfo;

    await this._subscriberBasicInfo.updateDependent(updatedDependentInfo);
    this.closeUpdateDependentModal();
  }

  constructor({ dependentDelegate, subscriberBasicInfo, formId }: DomainData) {
    this._dependentDelegate = dependentDelegate;
    this._subscriberBasicInfo = subscriberBasicInfo;
    this._formId = formId;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

export function useUpdateDependentModalStore({
  dependentDelegate,
  subscriberBasicInfo,
  formId,
}: DomainData): UpdateDependentModalStore | null {
  const [store, setStore] = useState<UpdateDependentModalStore | null>(
    () => new UpdateDependentModalStore({ dependentDelegate, subscriberBasicInfo, formId }),
  );

  useEffect(() => {
    autorun(() => {
      setStore(new UpdateDependentModalStore({ dependentDelegate, subscriberBasicInfo, formId }));
    });

    return (): void => {
      setStore(null);
    };
  }, [dependentDelegate, subscriberBasicInfo, formId]);
  return store;
}
